import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import StyledLink from '@makeship/core/lib/components/Link';
import posthog from 'posthog-js';
import Cookies from 'js-cookie';
import { subscribe } from '../../api/internal';
import { Overline, P2 } from '../Typography';
import { getProductIDFromShopifyGID, getStage } from '../../utils/product';
import { getCampaignType, getCreator, isUserLoggedIn, getSubscribeModalLocation } from '../../utils/analytics';
import { useStore } from '../../store';
import { CookieConsent } from '../../types/cookies';
import { ButtonPrimary } from '../../ui/atoms/Button';
import { useRecaptcha } from '../../utils/recaptcha';

const InputForm = styled.form<{ expanded: boolean }>`
  display: flex;
  flex-direction: ${({ expanded }) => (expanded ? 'column' : 'row')};
  width: 100%;
  align-items: center;
  padding-top: 8px;
  ${({ expanded }) =>
    expanded &&
    `
  > * + * {
    width: 100%;
    margin-bottom: 8px;
  }`}
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;

    > * + * {
      width: 100%;
      margin-bottom: 8px;
    }
  }
`;

const Input = styled.input<{ expanded: boolean }>`
  height: 44px;
  flex-grow: 1;
  flex-shrink: 1;
  padding: 0 8px;
  margin-right: ${({ expanded }) => (expanded ? '0' : '8px')};
  border: 1px solid ${({ theme }) => theme.colors.neutral7};
  min-width: 0;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    margin-right: 0;
  }
`;

const Message = styled.div`
  height: 44px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const LabelP2 = styled(P2)``;

const ErrorP2 = styled(P2)`
  color: ${({ theme }) => theme.colors.error};
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const CaptchaSubtext = styled(Overline)`
  color: ${({ theme }) => theme.colors.neutral4};
`;

const PrivacyPolicyLink = styled(StyledLink.Primary)``;

const TermsOfServiceLink = styled(StyledLink.Primary)``;

export type SubscribeProps = {
  label: string;
  expanded: boolean;
  ctaText: string;
  product?: Shopify.Product;
  tags?: string;
  setIsSubscribed?: React.Dispatch<React.SetStateAction<boolean>>;
  componentLocation?: string;
};

const errLabel = 'Could not subscribe! Check your email and try again.';

const Subscribe: React.FC<SubscribeProps> = ({
  label,
  expanded,
  ctaText,
  product,
  tags,
  setIsSubscribed,
  componentLocation,
}: SubscribeProps) => {
  const { state } = useStore();
  const { execute: executeRecaptcha } = useRecaptcha();
  const [captcha, setCaptcha] = useState('');
  const subscribeEmail = Cookies.get('subscribe_email');
  const [email, setEmail] = useState(`${state.user?.customer?.email || subscribeEmail || ''}`);
  const [subscribed, setSubscribed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const handlePosthogCapture = (eventName: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const location = componentLocation || getSubscribeModalLocation();
    const tagsWithLocation = tags ? `${tags}, ${location}` : location;

    const posthogCaptureProperties: Record<string, unknown> = {
      productID: product ? getProductIDFromShopifyGID(product.id || '') : 'unknown',
      event_location: location,
      product_name: product?.title || 'unknown',
      creator: product ? getCreator(product) : 'unknown',
      campaign_type: product ? getCampaignType(product) || '' : 'unknown',
      campaign_status: product ? getStage(product?.tags) : 'unknown',
      is_logged_in: isUserLoggedIn(state.user),
      cta_text: ctaText,
      tags: tagsWithLocation.split(',').map((value) => value.trim()),
    };

    posthog.capture(eventName, posthogCaptureProperties);
  };

  useEffect(() => {
    if (!captcha) {
      return;
    }
    const subscribeTags = tags;
    const cookieConsentAccepted = Cookies.get(CookieConsent.Functional) === 'true';
    if (cookieConsentAccepted) {
      Cookies.set('subscribe_email', email, { expires: 365 });
    }
    setIsLoading(true);
    handlePosthogCapture('subscribe_cta_click');
    subscribe(email, captcha, subscribeTags)
      .then(() => {
        handlePosthogCapture('user_subscribed');
        setSubscribed(true);
        setIsSubscribed?.(true);
      })
      .catch(() => {
        setError(true);
        setSubscribed(false);
        setIsSubscribed?.(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [captcha, tags]);

  const onSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const token = await executeRecaptcha();
      setCaptcha(token);
    } catch {
      setError(true);
      setIsLoading(false);
    }
  };

  if (subscribed) {
    return <Message data-gtm-name="success-message">Thank you for subscribing!</Message>;
  }

  return (
    <>
      {!error ? <LabelP2>{label}</LabelP2> : <ErrorP2>{errLabel}</ErrorP2>}
      <InputForm onSubmit={onSubscribe} expanded={expanded}>
        <FormColumn>
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.currentTarget.value);
            }}
            placeholder="example@email.com"
            required
            expanded={expanded}
          />
          <CaptchaSubtext>
            This site is protected by reCAPTCHA and the Google{' '}
            <PrivacyPolicyLink href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </PrivacyPolicyLink>{' '}
            and{' '}
            <TermsOfServiceLink href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </TermsOfServiceLink>{' '}
            apply.
          </CaptchaSubtext>
        </FormColumn>
        <ButtonPrimary className="self-start" disabled={isLoading} type="submit">
          {ctaText}
        </ButtonPrimary>
      </InputForm>
    </>
  );
};

export default Subscribe;
